import { createEffect, createMemo, createSignal, For, Show, untrack } from 'solid-js';
import Modal from './Modal';
import Text from './Text';
import { useTranslation } from '../hooks/useTranslation';
import {
    formatLocation,
    LocationKind,
    LOCATION_KIND_LABELS,
    LOCATION_KINDS,
    parseLocation
} from '../services/gameLocation';
import styles from './LocationPicker.module.css';

interface LocationPickerProps {
    value: string;
    onChange: (next: string) => void;
    optional?: boolean;
    disabled?: boolean;
}

// One picker for every "how does the bot get there" field. Free text used to be the only option, so
// nothing told a user which spellings the bot understands and a plot name and a /home command were
// indistinguishable. Here the kind is chosen, the name is typed, and the exact command is shown.
// Modal rather than showDialog, because the live command preview reacts to both fields as they change.
export default function LocationPicker(props: LocationPickerProps) {
    const { t } = useTranslation();

    const [open, setOpen] = createSignal(false);
    const [kind, setKind] = createSignal(LocationKind.Plot);
    const [name, setName] = createSignal('');

    const current = createMemo(() => parseLocation(props.value));

    createEffect(() => {
        if (!open()) return;
        untrack(() => {
            const existing = parseLocation(props.value);
            setKind(existing?.kind ?? LocationKind.Plot);
            setName(existing?.name ?? '');
        });
    });

    const draft = createMemo(() => ({ kind: kind(), name: name().trim() }));
    const preview = createMemo(() => (draft().name ? formatLocation(draft()) : ''));
    const isCustom = () => kind() === LocationKind.Custom;
    const valid = () => draft().name.length > 0 && (!isCustom() || draft().name.startsWith('/'));

    // Switching kind keeps what the user already typed wherever that still makes sense: a custom line
    // starts from the command it would have sent, and going back reuses the name inside it.
    const changeKind = (next: LocationKind) => {
        const typed = name().trim();
        if (next === LocationKind.Custom) {
            setName(typed ? formatLocation({ kind: kind(), name: typed }) : '');
        } else if (isCustom()) {
            const parsed = parseLocation(typed);
            setName(parsed && parsed.kind !== LocationKind.Custom ? parsed.name : '');
        }
        setKind(next);
    };

    const submit = () => {
        if (!valid()) return;
        props.onChange(formatLocation(draft()));
        setOpen(false);
    };

    const clear = () => {
        props.onChange('');
        setOpen(false);
    };

    return (
        <>
            <button type="button" class={styles.trigger} disabled={props.disabled}
                onClick={() => setOpen(true)}>
                <i class="fa-solid fa-map-marker-alt" />
                <Show when={current()} fallback={
                    <span class={styles.notSet}><Text val="LOCATION_NOT_SET" /></span>
                }>
                    <span class={styles.kind}><Text val={LOCATION_KIND_LABELS[current()!.kind]} /></span>
                    <span class={styles.command}>{formatLocation(current()!)}</span>
                </Show>
                <i class={`fa-solid fa-pen-to-square ${styles.editIcon}`} />
            </button>

            <Modal open={open()} onClose={() => setOpen(false)} maxWidth="34rem"
                title={<Text val="LOCATION_PICKER_TITLE" />}
                subtitle={<Text val="LOCATION_PICKER_SUBTITLE" />}
                leading={<i class="fa-solid fa-map-marker-alt" />}>
                <form class="v" style={{ gap: 'var(--spacing-lg)' }}
                    onSubmit={(e) => { e.preventDefault(); submit(); }}>

                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span class={styles.fieldLabel}><Text val="LOCATION_KIND" /></span>
                        <select value={kind()}
                            onChange={(e) => changeKind(Number(e.currentTarget.value) as LocationKind)}>
                            <For each={LOCATION_KINDS}>
                                {(k) => <option value={k}>{t(LOCATION_KIND_LABELS[k])}</option>}
                            </For>
                        </select>
                    </label>

                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span class={styles.fieldLabel}>
                            <Text val={isCustom() ? 'LOCATION_CUSTOM_COMMAND' : 'LOCATION_NAME'} />
                        </span>
                        <input type="text" value={name()} autocomplete="off"
                            onInput={(e) => setName(e.currentTarget.value)} />
                        <Show when={isCustom()}>
                            <span class={styles.hint}><Text val="LOCATION_CUSTOM_HINT" /></span>
                        </Show>
                    </label>

                    <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span class={styles.fieldLabel}><Text val="LOCATION_COMMAND_PREVIEW" /></span>
                        <Show when={preview()} fallback={
                            <span class={styles.notSet}><Text val="LOCATION_NOT_SET" /></span>
                        }>
                            <p class="commandtext">{preview()}</p>
                        </Show>
                    </div>

                    <div class="h" style={{ 'justify-content': 'space-between', gap: 'var(--spacing-sm)' }}>
                        <Show when={props.optional && !!current()} fallback={<span />}>
                            <button type="button" class="btn btn-secondary" onClick={clear}>
                                <Text val="LOCATION_CLEAR" />
                            </button>
                        </Show>
                        <div class="h" style={{ gap: 'var(--spacing-sm)' }}>
                            <button type="button" class="btn btn-secondary" onClick={() => setOpen(false)}>
                                <Text val="CANCEL" />
                            </button>
                            <button type="submit" class="btn btn-primary" disabled={!valid()}>
                                <Text val="SAVE" />
                            </button>
                        </div>
                    </div>
                </form>
            </Modal>
        </>
    );
}
